import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

const AuthCtx = createContext(null);
const USER_KEY = "deca_user_v2";

function readCachedUser() {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

function persistUser(user) {
  if (user && typeof user === "object") localStorage.setItem(USER_KEY, JSON.stringify(user));
  else localStorage.removeItem(USER_KEY);
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    if (typeof navigator !== "undefined" && !navigator.onLine) return readCachedUser() || null;
    return null;
  });
  const [error, setError] = useState("");

  const check = useCallback(async () => {
    try { localStorage.removeItem("deca_token"); } catch { /* ignore */ }
    const cached = readCachedUser();

    // Con red: la sesión la dice el servidor. La caché no inicia sesión.
    if (navigator.onLine) {
      try {
        const { data } = await api.get("/api/auth/me", { timeout: 8000 });
        persistUser(data);
        setUser(data);
      } catch {
        persistUser(null);
        setUser(false);
      }
      return;
    }

    setUser(cached || false);
  }, []);

  useEffect(() => {
    check();
  }, [check]);

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/api/auth/login", { email, password });
      persistUser(data.user);
      setUser(data.user);
      return data.user;
    } catch (e) {
      if (!e.response) {
        setError("Sin conexión. Entra una vez con internet y pulsa Sincronizar; después podrás reabrir la app sin cobertura.");
        return false;
      }
      const status = e.response.status;
      const d = e.response?.data?.detail;
      if (d != null && d !== "") {
        setError(formatApiErrorDetail(d));
      } else if (status === 429) {
        setError("Demasiados intentos fallidos. Prueba en 15 min.");
      } else if (status === 401) {
        setError("Credenciales inv" + String.fromCharCode(0xe1) + "lidas");
      } else {
        setError("Ha ocurrido un error. Int" + String.fromCharCode(0xe9) + "ntalo de nuevo.");
      }
      return false;
    }
  };

  const logout = async () => {
    try { await api.post("/api/auth/logout"); } catch (e) { /* ignore */ }
    persistUser(null);
    setUser(false);
  };

  const cambiarEmpresa = async (codigo) => {
    const actual = user?.empresa_activa || user?.empresa_id || "";
    if (!codigo || codigo === actual) return user;
    const { data } = await api.post("/api/auth/empresa-activa", { codigo });
    persistUser(data);
    setUser(data);
    window.location.reload();
    return data;
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, logout, refresh: check, cambiarEmpresa }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
